"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "kitchen-space-planner",
  "refrigerator-lifespan-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function WineRefrigeratorCalculatorPageClient() {
  const meta = TOOL_METADATA["wine-refrigerator-calculator"]

  const [bottles, setBottles] = useState("24")
  const [bottleType, setBottleType] = useState("Standard")
  const [dualZone, setDualZone] = useState("No")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const numBottles = readCount(bottles, { min: 1, max: 500 })
    if (numBottles === null) return null

    const multiplier = bottleType === "Champagne" ? 1.3 : bottleType === "Mixed" ? 1.15 : 1
    const capacity = Math.ceil(numBottles * multiplier)
    const finalCapacity = dualZone === "Yes" ? Math.ceil(capacity * 1.2) : capacity

    let recommendation: string
    if (finalCapacity <= 12) recommendation = "Small countertop wine cooler (6–12 bottles)"
    else if (finalCapacity <= 24) recommendation = "Medium wine cooler (12–24 bottles)"
    else if (finalCapacity <= 48) recommendation = "Large wine cooler (24–48 bottles)"
    else recommendation = "Extra-large or built-in wine refrigerator (48+ bottles)"

    return { capacity: finalCapacity, recommendation }
  }, [bottles, bottleType, dualZone])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What capacity does"
      heroTitleItalic="your"
      heroTitlePost="cellar deserve?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Sommelier sizing guidance"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "+30%", label: "Champagne adjust", note: "Wider bottles" },
            { value: "+20%", label: "Dual-zone adjust", note: "Divider loss" },
            { value: "55°F", label: "Long-term aging", note: "Cellar target" },
            { value: "45–55", label: "White serving °F", note: "Cool side" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "wine-refrigerator-calculator")}
      cta={{
        title: "Right capacity. Right zones. Right glass door.",
        body: "Browse single- and dual-zone wine coolers from compact 6-bottle units to 150-bottle built-ins.",
        buttonLabel: "Browse wine coolers",
        buttonHref: "/collections/wine-fridges",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Bottle count.
                <br />
                Bottle shape.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Champagne is 30% wider than Bordeaux. Dual-zone steals 20% of usable rack space.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Number of bottles
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={500}
                      step="1"
                      value={bottles}
                      onChange={(e) => setBottles(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">bottles</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Bottle type
                  </label>
                  <select
                    value={bottleType}
                    onChange={(e) => setBottleType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="Standard">Standard (Bordeaux / Burgundy)</option>
                    <option value="Champagne">Champagne (wider, +30%)</option>
                    <option value="Mixed">Mixed collection (+15%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Dual zone?
                  </label>
                  <select
                    value={dualZone}
                    onChange={(e) => setDualZone(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="No">No — single temperature</option>
                    <option value="Yes">Yes — reds + whites at correct temps</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Dual-zone is essential if you serve from the unit.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Recommended capacity
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.capacity ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">bottles</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendation ?? "Enter your collection details."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the cellar"
          title="Wine coolers in your range"
          description="Single- and dual-zone units with UV-protected glass and vibration dampening."
          collectionType="wine-cooler"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Bottle math, <em className="italic text-[#ff9951]">honest.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Manufacturer &quot;X-bottle capacity&quot; specs assume standard Bordeaux bottles. Champagne, Burgundy, and Pinot bottles are wider and reduce real capacity. We adjust for that.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Bottle-shape multiplier",
                  d: "Standard = 1.0×. Mixed = 1.15×. Champagne = 1.3× — you need more rack room than the spec sheet suggests.",
                },
                {
                  n: "02",
                  t: "Dual-zone adjustment",
                  d: "Adds 20% to the capacity number to account for the divider and reduced shelf efficiency on each side.",
                },
                {
                  n: "03",
                  t: "Form-factor recommendation",
                  d: "≤12 bottles → countertop. 12–24 → freestanding compact. 24–48 → undercounter / built-in. 48+ → tower or wine cellar.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges with bar-friendly layouts"
          description="If your cellar includes craft beer, mixers, and seltzers alongside wine, a beverage center makes more sense than a dedicated wine fridge."
          collectionType="counter-depth"
          fallbackCollectionType="wine-cooler"
        />
      </section>
    </ToolPageShell>
  )
}
